import React from 'react';

interface HomeSectionProps {
  id?: string;
  eyebrow?: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  as?: 'section' | 'div';
  /** Visual density — avoid identical card stacks across the page */
  tone?: 'default' | 'muted' | 'emphasis';
}

const toneClasses: Record<NonNullable<HomeSectionProps['tone']>, string> = {
  default: '',
  muted: 'bg-[#0c0c0c] border-y border-white/[0.05]',
  emphasis: 'bg-gradient-to-b from-orange-950/15 to-transparent',
};

export const HomeSection: React.FC<HomeSectionProps> = ({
  id,
  eyebrow,
  title,
  description,
  action,
  children,
  className = '',
  as: Tag = 'section',
  tone = 'default',
}) => (
  <Tag id={id} className={`py-12 sm:py-16 ${toneClasses[tone]} ${className}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-7 sm:mb-10">
        <div className="min-w-0">
          {eyebrow ? <p className="kicker mb-2.5">{eyebrow}</p> : null}
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white text-balance">
            {title}
          </h2>
          {description ? (
            <p className="text-gray-400 text-sm sm:text-base mt-2.5 max-w-2xl leading-relaxed">{description}</p>
          ) : null}
        </div>
        {action ? <div className="flex-shrink-0">{action}</div> : null}
      </div>
      {children}
    </div>
  </Tag>
);

export default HomeSection;
